// FulfillmentInventoryPanel -- Slice 1D.
//
// Read-only unit list filtered to inventory_holder_org_id = current
// org. Each row shows the unit's display_label, origin, and
// (if present) the intake-form status as a colored badge so the lot
// manager can see at a glance which units are stuck in intake review.
//
// Container/padding doctrine §K1+K2: opens with
// `<div className="w-full text-left space-y-4">`.

import { useState } from "react";
import type { IntakeFormStatus } from "@/lib/fulfillment/intakeFormStatus";
import type { FulfillmentUnit } from "@/lib/fulfillment/fulfillmentApi";
import { FIREFLY_TARGET_ATTR } from "@/lib/firefly/fireflyTargetConvention";
import { useFulfillmentInventory } from "./useFulfillmentInventory";
import { FulfillmentServiceRequestModal } from "./FulfillmentServiceRequestModal";

const STATUS_LABEL: Record<IntakeFormStatus, string> = {
  pending: "Intake: needs filling",
  awaiting_review: "Intake: needs review",
  work_in_progress: "Intake: work in progress",
  approved: "Intake approved",
};

const STATUS_CLASS: Record<IntakeFormStatus, string> = {
  pending:
    "bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-100",
  awaiting_review:
    "bg-blue-100 text-blue-900 dark:bg-blue-900/40 dark:text-blue-100",
  work_in_progress:
    "bg-purple-100 text-purple-900 dark:bg-purple-900/40 dark:text-purple-100",
  approved:
    "bg-green-100 text-green-900 dark:bg-green-900/40 dark:text-green-100",
};

function StatusBadge({ status }: { status: IntakeFormStatus | null }) {
  if (!status) return null;
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_CLASS[status]}`}
    >
      {STATUS_LABEL[status]}
    </span>
  );
}

function UnitRow({
  unit,
  onRequestService,
}: {
  unit: FulfillmentUnit;
  onRequestService: () => void;
}) {
  return (
    <li className="flex flex-wrap items-center justify-between gap-3 py-3">
      <div>
        <div className="flex items-center gap-2">
          <span className="font-mono text-sm text-gray-900 dark:text-zinc-100">
            {unit.display_label}
          </span>
          <span className="text-xs uppercase tracking-wide text-gray-500 dark:text-zinc-400">
            {unit.origin}
          </span>
        </div>
        {unit.notes ? (
          <p className="mt-1 text-sm text-gray-600 dark:text-zinc-400">{unit.notes}</p>
        ) : null}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={onRequestService}
          className="rounded-md border border-indigo-300 bg-indigo-50 px-2 py-1 text-xs font-semibold text-indigo-900 hover:bg-indigo-100 dark:border-indigo-700 dark:bg-indigo-950/40 dark:text-indigo-100"
        >
          Request service
        </button>
        <StatusBadge status={unit.intake_form_status} />
      </div>
    </li>
  );
}

export function FulfillmentInventoryPanel() {
  const m = useFulfillmentInventory();
  const [serviceUnit, setServiceUnit] = useState<FulfillmentUnit | null>(null);
  return (
    <div className="w-full text-left space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-lg font-bold text-gray-900 dark:text-zinc-100">
          Fulfillment — Inventory
        </h2>
        <button
          type="button"
          onClick={() => void m.refresh()}
          disabled={m.busy}
          className="rounded-md border border-gray-300 bg-white px-3 py-1 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-200 dark:hover:bg-zinc-800"
        >
          {m.busy ? "Refreshing…" : "Refresh"}
        </button>
      </div>

      {m.error ? (
        <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900 dark:border-red-800 dark:bg-red-950/35 dark:text-red-100">
          {m.error}
        </div>
      ) : null}

      <div
        {...{ [FIREFLY_TARGET_ATTR]: "ff-inventory-list" }}
        className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm dark:border-zinc-700 dark:bg-zinc-900"
      >
        {m.busy && m.units.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-zinc-400">Loading inventory…</p>
        ) : m.units.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-zinc-400">
            No units on this lot yet. Use <strong>New inventory</strong> to add a cash purchase or
            repo intake.
          </p>
        ) : (
          <ul className="divide-y divide-gray-100 dark:divide-zinc-800">
            {m.units.map((u) => (
              <UnitRow key={u.id} unit={u} onRequestService={() => setServiceUnit(u)} />
            ))}
          </ul>
        )}
      </div>

      {serviceUnit ? (
        <FulfillmentServiceRequestModal
          unit={serviceUnit}
          open={Boolean(serviceUnit)}
          onClose={() => setServiceUnit(null)}
        />
      ) : null}
    </div>
  );
}
